import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FileModel, HttpService, ProfileService, ScormBehaviourType, STORAGE_KEYS, StorageKeys, StorageService } from 'library-explorer';
import { BaseWarningModalComponent } from '../components/base-warning-modal/base-warning-modal.component';
import { ScormPackageResults } from '../model/scrom-package-data';

@Injectable({
  providedIn: 'root'
})
export class ScormApiService {
  public static readonly INCOMPLETE_SCORM_STATUS = 'incomplete';
  public static readonly INCOMPLETE_SCORM2004_STATUS = 'unknown';
  public static readonly PASSED_SCORM_STATUS = 'passed';
  public static readonly COMPLETED_SCORM_STATUS = 'completed';

  public packageInProgress = new BehaviorSubject<ScormPackageResults>(null);

  private get localStorageKey() {
    const userId = this.profileService.getCurrentProfileValue()?.id;

    return `${this.storageKeys.SCORM_SUSPEND_DATA}-${userId}`;
  }

  constructor(
    private readonly storageService: StorageService,
    @Inject(STORAGE_KEYS) private readonly storageKeys: StorageKeys,
    private readonly profileService: ProfileService,
    private readonly http: HttpService,
    private readonly dialog: MatDialog) { }

  public getScormPackageEmbedUrl(document: FileModel): string {
    const documentUrl = document.uri || document.url;

    if (!documentUrl) {
      return ''
    };

    const origin = new URL(documentUrl)?.origin;

    return `${origin}/_shared/_aws_public_/scorm_embed.html?packageUrl=${encodeURIComponent(documentUrl)}`;
  }

  public async canLeaveScorm(scormBehaviour: ScormBehaviourType): Promise<boolean> {
    const data = this.packageInProgress.value;
    if (data !== null && data.status !== ScormApiService.INCOMPLETE_SCORM_STATUS) {
      return true;
    }

    switch (scormBehaviour) {
      case ScormBehaviourType.WARN_NOT_COMPLETED:
        return await this.openWarningModal();
      case ScormBehaviourType.BLOCK_UNTIL_COMPLETED:
        return false;
      default:
        return true;
    }
  }

  public saveScormProgress(slideId: string, suspendData: string): Observable<void> {
    return this.http.post(`scorm-suspend-data/${slideId}`, { suspendData });
  }

  public getScormProgress(slideId: string): Observable<string> {
    return this.http.get<{ suspendData: string }>(`scorm-suspend-data/${slideId}`)
      .pipe(
        map(data => data.suspendData),
        map(suspendData => {
          if (suspendData) {
            return suspendData;
          }

          const storedSuspendData = this.getScormDataFromLocalStorage(slideId);
          if (storedSuspendData) {
            this.saveScormProgress(slideId, storedSuspendData).subscribe();
          }

          return storedSuspendData;
        })
      );
  }

  private openWarningModal(): Promise<boolean> | boolean {
    const dialog = this.dialog.open(BaseWarningModalComponent, {
      data: {
        message: `CHAPTERS.leave_the_scorm`,
        cancelBtnText: 'COMMON.cancel',
        okBtnText: 'COMMON.continue'
      }
    });

    return dialog.afterClosed().toPromise();
  }

  private getScormDataFromLocalStorage(id: string): string {
    const data = this.storageService.getItem<Array<{ id: string, data: string }>>(this.localStorageKey);
    const currentScorm = data?.find(item => item.id === id);

    return currentScorm?.data || '';
  }
}
